/**
 * SPA が読む環境変数（ARCHITECTURE.md §6.2）。
 *
 * `import.meta.env` を直接あちこちで読むと、変数名の綴り間違いが実行時まで
 * 分からない。読み取りはこのファイルだけに閉じ込める。
 */

interface PortalEnv {
  /** 別オリジン運用時の API ベース URL。空なら同一オリジン（dev は Vite proxy）。 */
  apiUrl: string;
  /** ヘッダ・タイトルに出すサービス名。 */
  brandName: string;
  /** Webメール（aurora-mail）のベース URL。ディープリンク取得に失敗したときの退避先。 */
  webmailUrl: string;
  /** ヘルプ / 問い合わせ導線。空なら導線を出さない。 */
  supportUrl: string;
  /**
   * 開発者ログインボタンの**ビルド時**フラグ（第 1 層）。
   * 実際に描画してよいかは `publicConfig.devLogin`（サーバ真実）との AND で決める。
   * 本番ビルドでは未設定になり、このフラグ自体が false になる。
   */
  devLogin: boolean;
  /**
   * メールソフト設定に出すホスト名の既定値。
   * 通常は API（`/portal/org` の `node.mailHostname`）が返す値を使い、
   * これはサーバーがまだ値を返せない dev 環境向けの退避値。
   */
  fallbackMailHostname: string;
}

const raw = import.meta.env as unknown as Record<string, string | undefined>;

function str(key: string, fallback = ''): string {
  const v = raw[key];
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

export const env: PortalEnv = {
  // 末尾スラッシュは付いていても付いていなくても動くよう、ここで削っておく。
  apiUrl: str('VITE_API_URL').replace(/\/+$/, ''),
  brandName: str('VITE_BRAND_NAME', 'Aurora Host'),
  webmailUrl: str('VITE_WEBMAIL_URL').replace(/\/+$/, ''),
  supportUrl: str('VITE_SUPPORT_URL'),
  // §6.1 の規約どおり真は "1" のみ。"true" や "yes" は無効値として false 扱いにする。
  // VITE_DEMO_LOGIN は公開デモ専用（サーバ側は DEMO_LOGIN_ENABLED で開く）。
  // どちらも立てずにビルドすればボタンの実体ごと成果物から落ちる。
  devLogin: raw.VITE_DEV_LOGIN === '1' || raw.VITE_DEMO_LOGIN === '1',
  fallbackMailHostname: str('VITE_MAIL_HOSTNAME'),
};
